/**
 * Helpers for building meta-store keys.
 *
 * The current dashboard uses these key conventions:
 *   weeklySummary  — "p{pi}-g{gi}"           (goal-level only; sub-goals don't get TLDR/Wins/Flags)
 *   fieldEdits     — "p{pi}-g{gi}-{field}"   or "p{pi}-g{gi}-s{si}-{field}"
 *   reactions      — "p{pi}-g{gi}"           or "p{pi}-g{gi}-s{si}"
 *   actionItems    — "p{pi}-g{gi}"
 *   comments[i].contextLabel — the goal name (not the p/g key)
 *
 * To keep components using human-readable identifiers (pillarName rather
 * than index), these helpers convert a pillarName → pi using the goals array.
 */

export function goalKey(pillarIndex, goalIndex) {
  return `p${pillarIndex}-g${goalIndex}`
}

export function subgoalKey(pillarIndex, goalIndex, subgoalIndex) {
  return `p${pillarIndex}-g${goalIndex}-s${subgoalIndex}`
}

export function fieldEditKey(pillarIndex, goalIndex, field, subgoalIndex) {
  if (typeof subgoalIndex === 'number') {
    return `p${pillarIndex}-g${goalIndex}-s${subgoalIndex}-${field}`
  }
  return `p${pillarIndex}-g${goalIndex}-${field}`
}

/**
 * Look up the pillar index for a pillar name in a goals data array.
 * Returns -1 if not found.
 */
export function pillarIndexByName(goalsData, pillarName) {
  if (!Array.isArray(goalsData)) return -1
  return goalsData.findIndex((p) => p.pillar === pillarName)
}

/**
 * Get the current ISO week-start key (YYYY-MM-DD of Monday).
 * Weekly summaries and snapshots are keyed by this.
 */
export function currentWeekKey(date = new Date()) {
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  // Move to Monday (ISO week: day 1 = Monday)
  const day = d.getDay() // 0 = Sunday, 1 = Monday, ...
  const diff = day === 0 ? -6 : 1 - day // if Sunday, go back 6 days
  d.setDate(d.getDate() + diff)
  return d.toISOString().slice(0, 10)
}

/**
 * Week-key for the Monday N weeks before `date`. Default is the prior week
 * (1 week ago). Used for snapshotting the week that just ended.
 */
export function weekKeyOffset(date = new Date(), weeksAgo = 1) {
  const thisMonday = currentWeekKey(date)
  const d = new Date(thisMonday)
  d.setDate(d.getDate() - 7 * weeksAgo)
  return d.toISOString().slice(0, 10)
}

/**
 * Human-readable label for a week-key.
 * "2026-04-14" → "Apr 14 – Apr 20, 2026"
 */
export function weekLabel(weekKey) {
  if (!weekKey) return ''
  const start = new Date(weekKey + 'T00:00:00')
  if (isNaN(start.getTime())) return weekKey
  const end = new Date(start)
  end.setDate(end.getDate() + 6)
  const startStr = start.toLocaleDateString([], { month: 'short', day: 'numeric' })
  const endStr = end.toLocaleDateString([], { month: 'short', day: 'numeric' })
  return `${startStr} – ${endStr}, ${start.getFullYear()}`
}

/**
 * Human-readable "X ago" — used for attribution timestamps.
 * Same rough logic as the current dashboard.
 */
export function timeAgo(timestamp) {
  if (!timestamp) return ''
  const ms = Date.now() - timestamp
  const mins = Math.floor(ms / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days < 7) return `${days}d ago`
  return new Date(timestamp).toLocaleDateString([], { month: 'short', day: 'numeric' })
}
